/**
 * Side panel that projects local form state into a structural preview of the
 * brief template. Pure FE projection — no new endpoints, no new mutations,
 * no `section.content` reflection (every section renders dummy lorem ipsum).
 *
 * Non-modal by design: the editor stays interactive while preview is open so
 * the admin can edit and observe simultaneously. The yellow callout at the
 * top of the body signals the dummy-content contract on every open.
 */
import { Fragment } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { MessageCircleWarningIcon, XIcon } from 'lucide-react';
import { BriefTemplatePreviewEmptyState } from './BriefTemplatePreviewEmptyState';
import type { BriefTemplatePreviewPanelProps } from './BriefTemplatePreviewPanel.types';
import { BriefTemplatePreviewSection } from './BriefTemplatePreviewSection';
import { useTranslation } from 'react-i18next';

export function BriefTemplatePreviewPanel(props: BriefTemplatePreviewPanelProps) {
  const { t } = useTranslation('knowledge');

  // Empty-name rows are still being authored — hiding them from the preview
  // keeps the rehearsal honest (a section without a heading isn't a section
  // yet). Caller already filters tombstones via `liveSections`.
  const renderableSections = props.sections.filter((section) => section.name.trim() !== '');

  return (
    // Half the builder row, an `@container`. A percentage would resolve against the
    // reveal box around the panel, which shrinks as it closes, and re-wrap the content.
    <aside
      aria-label={t('knowledgeHub.briefTemplatePreviewPanel.templatePreview')}
      data-testid="brief-template-preview-panel"
      className="flex min-h-0 w-[50cqw] shrink-0 flex-col"
    >
      <Card className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden rounded-lg p-0 shadow-none">
        {/*
         * Header mirrors the prototype — centered title with the close
         * affordance absolutely anchored to the right edge so the panel
         * name stays optically centered regardless of label length.
         */}
        <header className="relative flex items-center justify-center border-b border-border-light px-6 py-3">
          <h2 className="text-base font-medium">
            {t('knowledgeHub.briefTemplatePreviewPanel.templatePreview')}
          </h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('knowledgeHub.briefTemplatePreviewPanel.closeTemplatePreview')}
                  onClick={props.onClose}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <XIcon aria-hidden className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {t('knowledgeHub.briefTemplatePreviewPanel.closeTemplatePreview')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </header>

        <CardContent className="flex flex-col gap-6 overflow-y-auto px-10 py-6">
          {/* Alert ships no `warning` variant, but the design system does ship the tokens —
                composed here rather than reaching for Tailwind's raw palette, which has no
                dark pairing and would invert badly. */}
          <Alert
            variant="default"
            iconPosition="left"
            icon={<MessageCircleWarningIcon aria-hidden />}
            data-testid="brief-template-preview-dummy-alert"
            className="border-warning bg-warning text-warning-foreground"
          >
            <AlertDescription className="text-warning-foreground">
              {t('knowledgeHub.briefTemplatePreviewPanel.attentionThisIsDummyContent')}
            </AlertDescription>
          </Alert>

          {renderableSections.length === 0 ? (
            <BriefTemplatePreviewEmptyState />
          ) : (
            renderableSections.map((section, index) => (
              <Fragment key={section.tempId}>
                {index > 0 && <Separator className="my-2" />}
                <BriefTemplatePreviewSection section={section} />
              </Fragment>
            ))
          )}
        </CardContent>
      </Card>
    </aside>
  );
}
